import React from 'react';
import { motion } from 'framer-motion';
import { fadeInUp, staggerContainer } from '../hooks/useScrollAnimation';
interface TimelineItemProps {
  year: string;
  role: string;
  company: string;
  scope: string;
  description: string;
  bullets: string[];
  isLeft: boolean;
}
function TimelineItem({
  year,
  role,
  company,
  scope,
  description,
  bullets,
  isLeft
}: TimelineItemProps) {
  return <div className={`flex flex-col md:flex-row items-start justify-between w-full mb-12 relative ${isLeft ? 'md:flex-row-reverse' : ''}`} data-id="element-87">
      <motion.div variants={fadeInUp} className="w-full md:w-[45%] bg-dark-card border border-dark-border p-6 rounded-xl relative z-10" data-id="element-88">
        <div className="font-mono text-gold text-sm mb-1" data-id="element-89">{year}</div>
        <h3 className="text-lg font-bold text-white mb-0.5 leading-snug" data-id="element-90">
          {role}
        </h3>
        <div className="text-gold/70 font-semibold mb-1" data-id="element-91">{company}</div>
        <div className="text-cyan/70 text-xs font-mono mb-3" data-id="element-92">{scope}</div>
        <p className="text-slate-400 text-sm leading-relaxed mb-3" data-id="element-93">
          {description}
        </p>
        <ul className="space-y-1.5" data-id="element-94">
          {bullets.map((b, i) => <li key={i} className="flex items-start gap-2 text-slate-400 text-xs leading-relaxed" data-id="element-95">
              <span className="w-1 h-1 rounded-full bg-gold/60 mt-1.5 flex-shrink-0" data-id="element-96"></span>
              {b}
            </li>)}
        </ul>
        <div className={`hidden md:block absolute top-8 w-8 h-[1px] bg-gold/30 ${isLeft ? '-right-8' : '-left-8'}`} data-id="element-97"></div>
      </motion.div>
      <div className="absolute left-0 md:left-1/2 transform md:-translate-x-1/2 w-4 h-4 rounded-full bg-dark-bg border-2 border-gold/60 z-20 mt-8 md:mt-8" data-id="element-98"></div>
      <div className="hidden md:block w-[45%]" data-id="element-99"></div>
    </div>;
}
export function CareerTimeline() {
  const roles = [{
    year: '2025 — Present',
    role: 'EVP & Chief Technology Officer, Hosting Services',
    company: 'TRUIST',
    scope: '$850M budget · 950+ global staff · Dallas, TX',
    description: 'Enterprise accountability for integrated compute, storage, database, middleware, infrastructure security engineering, compliance management, and data center services through product-based operating models.',
    bullets: ['Driving enterprise hybrid cloud strategy integrating public cloud with modernized private cloud infrastructure', 'Expanding next-generation data center capabilities for AI workloads and geographic resiliency', 'Redefining enterprise resiliency models to enable accelerated failover and operational continuity', 'Advancing Infrastructure-as-Code and AI-enabled provisioning automation strategy', 'Leading zero-trust aligned network segmentation and secure backbone redesign', 'Modernizing database and middleware ecosystems toward streaming-based architectures']
  }, {
    year: '2022 — 2025',
    role: 'EVP & Chief Technology Officer, Foundational Hosting Platform',
    company: 'WELLS FARGO',
    scope: '$1.2B budget · 1,700+ global staff · Dallas, TX',
    description: 'Enterprise responsibility for core infrastructure lifecycle management, cyber resiliency, disaster recovery, data center strategy, and compliance oversight across global operations.',
    bullets: ['Built four new enterprise data centers while exiting eight legacy facilities', 'Delivered $60.8M in annualized efficiency savings', 'Achieved eight effective regulatory audit ratings with no adverse findings', 'Implemented enterprise-wide data immutability strategy protecting distributed and mainframe environments', 'Enabled automated provisioning velocity of 50,000 virtual machines annually', 'Redesigned core compute and storage platforms for cloud-like workload placement at enterprise scale']
  }, {
    year: '2019 — 2022',
    role: 'Vice President, Core Infrastructure Services',
    company: 'USAA',
    scope: '$500M budget · 1,000+ staff · Plano, TX',
    description: 'Led enterprise infrastructure transformation including data centers, network services, private and public cloud platforms, database environments, and availability command center operations.',
    bullets: ['Built new enterprise data center and migrated 25% of application portfolio', 'Delivered $80M in cost reduction through software-defined data center redesign', 'Improved enterprise availability metrics to highest performance levels in two years', 'Achieved FFIEC compliance and successfully closed OCC regulatory actions', 'Established enterprise Infrastructure-as-Code platform enabling automation at scale', 'Implemented offline storage resiliency strategy for ransomware preparedness']
  }, {
    year: '2000 — 2004',
    role: 'SVP & Chief Technology Officer, Network and Workplace Services',
    company: 'AIG',
    scope: '$200M+ budget · 300+ staff · Fort Worth, TX',
    description: 'Directed global network services strategy including architecture, engineering, operations, and enterprise security alignment.',
    bullets: ['Delivered $6M annual savings through strategic WAN contract renegotiation', 'Modernized 330+ global sites including network and collaboration platforms', 'Established multi-region cloud network architecture', 'Closed 43 federal audit deficiencies and strengthened compliance posture', 'Implemented software-defined data center architecture with next-generation virtualization']
  }];
  const earlierRoles = [{
    company: 'General Motors',
    title: 'IT Director, Global Network Engineering',
    years: '2013–2016'
  }, {
    company: 'Hewlett Packard',
    title: 'Senior IT Director, Global Telecom',
    years: '2010–2013'
  }, {
    company: 'Verizon',
    title: 'Senior Sales Engineer',
    years: '2009–2010'
  }, {
    company: 'Dell',
    title: 'Network Engineering Manager',
    years: '2000–2009'
  }];
  return <section id="career-timeline" className="py-20 md:py-32 bg-dark-bg relative" data-id="element-100">
      <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-101">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} data-id="element-102">
          <motion.div variants={fadeInUp} className="mb-16 text-center" data-id="element-103">
            <span className="font-sans text-gold/80 tracking-widest uppercase text-xs font-semibold mb-2 block" data-id="element-104">
              Career Trajectory
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white" data-id="element-105">
              Professional History
            </h2>
          </motion.div>

          <div className="relative mb-16" data-id="element-106">
            <div className="absolute left-2 md:left-1/2 top-0 bottom-0 w-[2px] bg-gradient-to-b from-gold/10 via-gold/30 to-gold/10 transform md:-translate-x-1/2" data-id="element-107"></div>
            <div className="pl-8 md:pl-0" data-id="element-108">
              {roles.map((role, index) => <TimelineItem key={index} {...role} isLeft={index % 2 !== 0} data-id="element-109" />)}
            </div>
          </div>

          {/* Earlier Career */}
          <motion.div variants={fadeInUp} className="bg-dark-card border border-dark-border rounded-xl p-8 max-w-4xl mx-auto" data-id="element-110">
            <h3 className="text-white font-bold text-base mb-5 pb-4 border-b border-dark-border" data-id="element-111">
              Earlier Career
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-112">
              {earlierRoles.map((r, i) => <div key={i} className="flex items-start gap-3" data-id="element-113">
                  <span className="w-1.5 h-1.5 rounded-full bg-gold/60 mt-1.5 flex-shrink-0" data-id="element-114"></span>
                  <div data-id="element-115">
                    <span className="text-white text-sm font-semibold" data-id="element-116">
                      {r.company}
                    </span>
                    <span className="text-slate-400 text-sm" data-id="element-117"> — {r.title}</span>
                    <div className="text-slate-600 text-xs mt-0.5 font-mono" data-id="element-118">
                      {r.years}
                    </div>
                  </div>
                </div>)}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}